import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { Popover } from "flowbite-react";
import {
  User,
  Sparkles,
  Settings,
  LogOut,
  AlertTriangle,
  Package,
  Moon,
  Sun,
  HelpCircle,
} from "lucide-react";
import { AdminSpaceLink } from "@/features/auth/components/AdminSpaceLink";
import { usePlan } from "@/features/auth/hooks/usePlan";
import { ORDERS_PATH } from "@/features/boutique/routes";
import { CartIcon } from "@/features/cart";
import { CartStickyBar } from "@/features/cart/components/CartStickyBar";
import { useCartContext } from "@/features/cart/context/CartContext";
import { CseMessages } from "@/features/cse-messages";
import { AppSidebar } from "@/features/navigation/components/AppSidebar";
import { MegaMenu } from "@/features/navigation/components/MegaMenu/MegaMenu";
import MobileUserProfil from "@/features/navigation/components/MobileUserProfil";
import {
  NotificationBell,
  NotificationMobileToggle,
  useNotificationsRefetch,
} from "@/features/notifications";
import { SponsorshipCtaButton } from "@/features/sponsorship/components/SponsorshipCtaButton";
import { useHasSponsorshipAccess } from "@/features/sponsorship/hooks/useHasSponsorshipAccess";
import { SPONSORSHIP_PATH } from "@/features/sponsorship/routes";
import { SubventionsIcon } from "@/features/subventions";
import { useNavigationPosition } from "@/features/theme";
import { AnimatedOrganizationBrand } from "@/shared/components/AnimatedOrganizationBrand";
import { Avatar } from "@/shared/components/Avatar";
import { EventNotificationHandler } from "@/shared/components/EventNotificationHandler";
import { ImpersonationBanner } from "@/shared/components/ImpersonationBanner";
import { useThemeContext } from "@/shared/components/theme/ThemeContext";
import ThemeToggle from "@/shared/components/theme/ThemeToggle";
import {
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/shared/components/ui/sidebar";
import { SidebarMobileButton } from "@/shared/components/ui/sidebar-mobile-button";
import { useAppSettings } from "@/shared/hooks/useAppSettings";
import { useUser } from "@/shared/hooks/useUser";
import { useUserMandatoryFieldsCheck } from "@/shared/hooks/useUserMandatoryFieldsCheck";
import { cn } from "@/shared/utils/style.util";

export type MainBackground = "default" | "muted";

type MainLayoutProps = {
  children: React.ReactNode
  background?: MainBackground
  wide?: boolean
};

function MainLayoutContent({ children, background = "default", wide = false }: MainLayoutProps) {
  const location = useLocation();
  const plan = usePlan();
  const { data: appSettings } = useAppSettings();
  const hiddenFeatures = appSettings?.hiddenFeatures ?? [];
  const hasShopAccess = !!plan?.features.service_eshop && !hiddenFeatures.includes("service_eshop");
  const hasDealsAccess = !!plan?.features.service_deals && !hiddenFeatures.includes("service_deals");
  const hasSponsorshipAccess = useHasSponsorshipAccess();
  const { cart } = useCartContext();
  const { theme, toggleTheme } = useThemeContext();
  const {
    isMobile,
    open: isSidebarOpen,
    openMobile,
    setOpen,
    setOpenMobile,
  } = useSidebar();
  const isSidebarOrDrawerOpen = isMobile ? openMobile : isSidebarOpen;

  const navigationPosition = useNavigationPosition();
  const isMegaMenu = !isMobile && navigationPosition === "top";

  useNotificationsRefetch();

  const isOnBlogPage = location.pathname.startsWith("/blog");
  // Vues transverses sous `/boutique/*` qui ne sont PAS de la
  // boutique au sens « catalogue produits » :
  //   - `/boutique/commandes` : Mes commandes (boutique + bons plans + locations)
  //   - `/boutique/panier`    : panier unifié (idem)
  // Le drawer Boutique ne doit pas auto-ouvrir sur ces pages, peu
  // importe `?cartType=` / `?orderType=`.
  const isOnTransverseBoutiquePage =
    location.pathname.startsWith("/boutique/commandes")
    || location.pathname.startsWith("/boutique/panier");
  const isOnBoutiquePage = location.pathname.startsWith("/boutique") && !isOnTransverseBoutiquePage;
  const isOnCheckoutPage = location.pathname === "/boutique/camping/checkout";
  const isOnDealsPage = location.pathname.startsWith("/bon-plans");

  // In MegaMenu mode, filter drawers (SecondLevelDrawer) are forced open on blog/boutique/deals pages.
  // Since the drawer is fixed-positioned (portal), we need to push the main content to avoid overlap.
  const hasOpenDrawerInMegaMenu = isMegaMenu && !isOnCheckoutPage && (isOnBlogPage || isOnBoutiquePage || isOnDealsPage);

  useEffect(() => {
    if (isOnCheckoutPage) {
      if (isMobile) {
        setOpenMobile(false);
      }
      else {
        setOpen(false);
      }
      return;
    }
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [location.pathname, isOnCheckoutPage, isMobile]);

  const closeSidebar = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const {
    first_name, last_name, email, avatar, initials, organization, linked_representative_id,
  } =
    useUser();
  const { hasMandatoryMissingFields, isLoading: isMandatoryFieldsLoading } =
    useUserMandatoryFieldsCheck();

  const showCartBar = Boolean(hasShopAccess && cart?.items?.length);

  const [hasScrolled, setHasScrolled] = useState(false);
  const scrollHandlerRef = useRef(() => setHasScrolled(window.scrollY > 0));
  scrollHandlerRef.current = () => setHasScrolled(window.scrollY > 0);

  useEffect(() => {
    const handler = () => scrollHandlerRef.current?.();
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const mobileSections = isMobile
    ? (
      <div className="px-2 py-2 space-y-2">
        {hasSponsorshipAccess && (
          <Link to={SPONSORSHIP_PATH} onClick={() => setOpenMobile(false)}>
            <SidebarMobileButton className="px-4 py-2 bg-[linear-gradient(135deg,#3ee8c8,#5bb8f5,#6b6ce0)] text-white rounded-lg">
              <div className="flex items-center gap-2 w-full">
                <Sparkles size={16} />
                <span className="text-xs font-semibold">Recommander un CSE</span>
              </div>
            </SidebarMobileButton>
          </Link>
        )}
        <SidebarMobileButton onClick={toggleTheme} className="px-4 py-2">
          <div className="flex items-center gap-2 w-full">
            {theme === "dark" ? <Moon size={20} /> : <Sun size={20} />}
            <span className="text-xs font-medium">
              Thème:
              {" "}
              {theme === "dark" ? "Sombre" : "Clair"}
            </span>
          </div>
        </SidebarMobileButton>
        <NotificationMobileToggle />
        <MobileUserProfil />
      </div>
    )
    : undefined;

  return (
    <>
      {/* Sidebar — always rendered. In MegaMenu mode, visually hidden but still mounted
          so that blog/boutique/deals filter drawers (SecondLevelDrawer portals) still work. */}
      {isMegaMenu
        ? (
          <div className="invisible absolute pointer-events-none" aria-hidden="true">
            <AppSidebar
              mobileSections={mobileSections}
              forceOpenBlogDrawer={isOnBlogPage}
              forceOpenBoutiqueDrawer={isOnBoutiquePage}
              forceOpenDealsDrawer={isOnDealsPage}
            />
          </div>
        )
        : (
          <AppSidebar
            mobileSections={mobileSections}
            onMainNavigationClose={isMobile ? closeSidebar : undefined}
            forceOpenBlogDrawer={isMobile && isOnBlogPage && isSidebarOrDrawerOpen}
            forceOpenBoutiqueDrawer={
              isMobile && isOnBoutiquePage && isSidebarOrDrawerOpen
            }
            forceOpenDealsDrawer={isMobile && isOnDealsPage && isSidebarOrDrawerOpen}
          />
        )}

      <div className="flex min-w-0 flex-1 flex-col isolate overflow-x-hidden">
        <ImpersonationBanner />
        {/* Navigation bar */}
        <nav
          className={cn(
            "bg-white px-4 py-2.5 dark:bg-gray-800 sticky top-0 z-50 transition-shadow duration-200",
            hasScrolled &&
            "border-b border-gray-200/60 dark:border-gray-700/60 shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:shadow-[0_1px_3px_0_rgba(0,0,0,0.15)]",
          )}
        >
          <div className="flex flex-wrap justify-between items-center gap-2 min-w-0">
            <div
              className={cn(
                "flex items-center min-w-0 gap-2 flex-shrink-0",
                "w-full md:w-auto",
                !isMobile && "justify-between",
              )}
            >
              {isMegaMenu
                ? (
                  <Link to="/" className="flex items-center gap-2">
                    <AnimatedOrganizationBrand isCollapsed={false} compact />
                  </Link>
                )
                : (
                  <>
                    <SidebarTrigger className="w-10 h-10 p-2 flex shrink-0 justify-center items-center rounded-full focus:ring-0 transition-colors duration-200 text-lightmuted hover:bg-gray-100 dark:hover:bg-gray-700" />
                    <span className="text-foreground truncate max-w-[140px] sm:max-w-[180px] lg:max-w-[200px]">
                      {organization?.name}
                    </span>
                  </>
                )}
            </div>
            <div className="flex items-center min-w-0 flex-1 justify-end gap-0 md:gap-2 overflow-hidden">
              {!isMobile && hasSponsorshipAccess && <SponsorshipCtaButton />}
              {!isMobile && <ThemeToggle />}
              {!isMobile && isMegaMenu && (
                <Link
                  to="/aide"
                  aria-label="Besoin d'aide ?"
                  title="Besoin d'aide ?"
                  className="w-10 h-10 p-2 flex shrink-0 justify-center items-center rounded-full text-lightmuted hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  <HelpCircle className="w-5 h-5" />
                </Link>
              )}
              {!isMobile && <NotificationBell />}
              {!isMobile && hasShopAccess && <SubventionsIcon />}
              {!isMobile && hasShopAccess && <CartIcon />}

              {/* User Menu Popover - Desktop only */}
              {!isMobile && (
                <>
                  <span className="truncate min-w-0 max-w-[100px] sm:max-w-[140px] lg:max-w-[180px] mx-1">
                    {first_name}
                    {" "}
                    {last_name}
                  </span>
                  <Popover
                    aria-labelledby="user-popover"
                    content={(
                      <div className="w-56 text-sm text-gray-500 dark:text-gray-400">
                        <div className="py-3 px-4 border-b border-gray-100 dark:border-gray-600">
                          <span className="block text-sm font-semibold text-foreground">{`${first_name} ${last_name}`}</span>
                          <span className="block text-sm text-foreground truncate">
                            {email}
                          </span>
                        </div>
                        <ul className="py-1 text-gray-700 dark:text-gray-300">
                          <li>
                            <Link
                              to="/mon-profil?tab=profil"
                              className="flex items-center py-2 px-4 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-400 dark:hover:text-white"
                            >
                              <User className="mr-2 w-4 h-4" />
                              Mon profil
                            </Link>
                          </li>
                          {(hasShopAccess || hasDealsAccess) && (
                            <li>
                              <Link
                                to={ORDERS_PATH}
                                className="flex items-center py-2 px-4 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-400 dark:hover:text-white"
                              >
                                <Package className="mr-2 w-4 h-4" />
                                Mes commandes
                              </Link>
                            </li>
                          )}
                          <li>
                            <Link
                              to="/mon-profil?tab=autorisations"
                              className={`flex items-center py-2 px-4 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white ${hasMandatoryMissingFields &&
                              !isMandatoryFieldsLoading
                                ? "text-red-600 dark:text-red-500 hover:text-red-700 dark:hover:text-red-400"
                                : "dark:text-gray-400"
                              }`}
                            >
                              <Settings className="mr-2 w-4 h-4" />
                              <span className="flex-1">
                                Paramètres du compte
                              </span>
                              {hasMandatoryMissingFields &&
                                !isMandatoryFieldsLoading && (
                                <AlertTriangle className="w-4 h-4 flex-shrink-0 ml-auto" />
                              )}
                            </Link>
                          </li>
                        </ul>
                        {linked_representative_id && (
                          <ul className="py-1 text-gray-700 dark:text-gray-300 border-t border-gray-100 dark:border-gray-600">
                            <li>
                              <AdminSpaceLink className="flex items-center py-2 px-4 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-400 dark:hover:text-white" />
                            </li>
                          </ul>
                        )}
                        <ul className="py-1 text-gray-700 dark:text-gray-300 border-t border-gray-100 dark:border-gray-600">
                          <li>
                            <Link
                              to="/deconnexion"
                              className="flex items-center py-2 px-4 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
                            >
                              <LogOut className="mr-2 w-4 h-4" />
                              Se déconnecter
                            </Link>
                          </li>
                        </ul>
                      </div>
                    )}
                    trigger="click"
                    placement="bottom-end"
                  >
                    <button
                      type="button"
                      className="flex mx-3 text-sm bg-gray-800 rounded-full md:mr-0 focus:ring-4 focus:ring-primary/10 dark:focus:ring-lightprimary cursor-pointer"
                      id="user-menu-button"
                      aria-label="Ouvrir le menu utilisateur"
                      aria-expanded="false"
                    >
                      <Avatar avatar={avatar} initials={initials} />
                    </button>
                  </Popover>
                </>
              )}
            </div>
          </div>
        </nav>

        {/* MegaMenu — only in megamenu mode */}
        {isMegaMenu && <MegaMenu hasOpenDrawer={hasOpenDrawerInMegaMenu} />}

        <main
          className={cn(
            "min-w-0 flex-1 w-full max-w-full overflow-x-hidden h-auto",
            background === "muted" && "bg-lightgray dark:bg-transparent",
            showCartBar ? "pb-28" : "",
            isMegaMenu && "transition-[margin] duration-300 ease-in-out",
            hasOpenDrawerInMegaMenu && "ml-[286px] w-[calc(100%-286px)]",
          )}
        >
          <div className={cn(wide ? "container-dashboard" : "container", "min-w-0")}>
            <CseMessages />
            {children}
          </div>
        </main>

        {showCartBar && cart && <CartStickyBar cart={cart} />}
        <EventNotificationHandler />
      </div>
    </>
  );
}

const MainLayout = ({ children, background, wide }: MainLayoutProps) => {
  return (
    <SidebarProvider defaultOpen>
      <MainLayoutContent background={background} wide={wide}>{children}</MainLayoutContent>
    </SidebarProvider>
  );
};

MainLayout.displayName = "MainLayout";

export default MainLayout;
